import { useCallback, useLayoutEffect, useRef, useState, type RefObject } from "react";
import { useStoreApi } from "@xyflow/react";
import { isCanvasShortcutTarget, shortcutModifier } from "./canvas-shortcuts";
import type { CanvasMode } from "./canvas-navigation";

/** Own only transient input state; graph/session orchestration stays in the page. */
export function useCanvasNavigation(options: {
  canvas: RefObject<HTMLDivElement | null>; resetKey: string; onCancel: () => void;
}) {
  const store = useStoreApi();
  const latest = useRef(options); latest.current = options;
  const [mode, setMode] = useState<CanvasMode>("select");
  const [temporaryPan, setTemporaryPan] = useState(false);
  const [gestureGeneration, setGestureGeneration] = useState(0);
  const persistent = useRef<CanvasMode>("select"), held = useRef(false), epoch = useRef(0);
  const isPanning = useCallback(() => persistent.current === "pan" || held.current, []);
  const clearStore = useCallback(() => {
    store.getState().cancelConnection();
    store.setState({ connectionClickStartHandle: null, multiSelectionActive: false,
      userSelectionRect: null, userSelectionActive: false, nodesSelectionActive: false });
  }, [store]);
  const cancel = useCallback(() => {
    epoch.current++;
    setGestureGeneration(epoch.current);
    held.current = false; setTemporaryPan(false);
    latest.current.onCancel();
    clearStore();
  }, [clearStore]);
  const release = useCallback(() => {
    if (held.current) cancel();
    else store.setState({ multiSelectionActive: false });
  }, [cancel, store]);
  const changeMode = useCallback((next: CanvasMode) => {
    cancel(); persistent.current = next; setMode(next);
  }, [cancel]);
  const holdSpace = useCallback(() => {
    if (held.current) return;
    cancel(); held.current = true; setTemporaryPan(true);
  }, [cancel]);
  const updateModifiers = useCallback((event: { target: EventTarget | null; shiftKey: boolean; metaKey: boolean; ctrlKey: boolean; altKey: boolean }) => {
    store.setState({ multiSelectionActive: !isPanning() && !event.altKey &&
      isCanvasShortcutTarget(event.target, latest.current.canvas.current) &&
      (event.shiftKey || shortcutModifier(event)) });
  }, [isPanning, store]);

  useLayoutEffect(() => { cancel(); }, [options.resetKey, cancel]);
  useLayoutEffect(() => {
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.key === " ") release();
      // Global listeners release only: acquiring a modifier is canvas-scoped.
      if (!event.shiftKey && !shortcutModifier(event)) store.setState({ multiSelectionActive: false });
    };
    const onFocus = (event: FocusEvent) => {
      if (!isCanvasShortcutTarget(event.target, latest.current.canvas.current)) cancel();
    };
    const onHidden = () => { if (document.hidden) cancel(); };
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", cancel);
    document.addEventListener("focusin", onFocus);
    document.addEventListener("visibilitychange", onHidden);
    return () => {
      window.removeEventListener("keyup", onKeyUp); window.removeEventListener("blur", cancel);
      document.removeEventListener("focusin", onFocus); document.removeEventListener("visibilitychange", onHidden);
      epoch.current++; held.current = false;
      latest.current.onCancel(); clearStore();
    };
  }, [release, cancel, clearStore, store]);
  return { mode, temporaryPan, gestureGeneration, changeMode, holdSpace, cancel, updateModifiers, isPanning,
    isGestureCurrent: useCallback((generation: number) => generation === epoch.current, []) };
}
